/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import type { TypeManufacturersProductsStore } from 'stores/ManufacturersProductsStore.js';

import ChipList from 'components/ChipList/ChipList';

import styles from './ManufacturerDetails.module.scss';

type InjectedProps = {
  manufacturersProductsStore: TypeManufacturersProductsStore,
} & TranslatorProps;

const statusClassObj: Object = {
  approved: 'approved',
  'not approved': 'not-approved',
  'pending review': 'pending',
  archived: 'archived',
};

@translate()
@inject('manufacturersProductsStore')
@observer
export default class ManufacturerDetails extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  componentDidMount() {
    const { manufacturersProductsStore } = this.props;

    const manufacturer = manufacturersProductsStore.selectedManufacturer;

    if (
      manufacturer.product_pending_count === 0 &&
      manufacturer.product_approved_count === 0 &&
      manufacturer.product_not_approved_count === 0 &&
      manufacturer.product_archived_count === 0
    ) {
      manufacturersProductsStore.setSelectedProductStatusEmpty();
    }
  }

  renderAliases() {
    const {
      manufacturersProductsStore: { selectedManufacturer: manufacturer },
      t,
    } = this.props;
    const chips = manufacturer.aliases.map(alias => ({ label: alias.alias }));

    if (manufacturer.aliases.length) {
      return <ChipList chips={chips} />;
    }

    return t('general:none');
  }

  renderDetails() {
    const {
      manufacturersProductsStore: { selectedManufacturer: manufacturer },
      t,
    } = this.props;
    const statusClass = statusClassObj[manufacturer.status.toLowerCase()];

    if (manufacturer.status && manufacturer.status === 'Pending Review') {
      return (
        <table className={styles.details}>
          <tbody>
            <tr>
              <td>
                <b>{t('general:labels.status')}:</b>
              </td>
              <td>
                <span className={styles[statusClass]}>{manufacturer.status}</span>
              </td>
            </tr>
            <tr>
              <td>
                <b>{t('general:manufacturers.labels.code')}:</b>
              </td>
              <td>Information required</td>
            </tr>
            <tr>
              <td>
                <b>{t('general:manufacturers.labels.short_code')}:</b>
              </td>
              <td>Information required</td>
            </tr>
            <tr>
              <td>
                <b>{t('general:manufacturers.labels.alias_plural')}:</b>
              </td>
              <td>{this.renderAliases()}</td>
            </tr>
            <tr>
              <td>
                <b>{t('general:manufacturers.labels.key_oem')}:</b>
              </td>
              <td>Not set</td>
            </tr>
            <tr>
              <td>
                <b>{t('general:manufacturers.labels.global_list')}:</b>
              </td>
              <td>{manufacturer.global_list ? 'Yes' : 'No'}</td>
            </tr>
          </tbody>
        </table>
      );
    }

    return (
      <table className={styles.details}>
        <tbody>
          <tr>
            <td>
              <b>{t('general:labels.status')}:</b>
            </td>
            <td>
              <span className={styles[statusClass]}>{manufacturer.status}</span>
            </td>
          </tr>
          <tr>
            <td>
              <b>{t('general:manufacturers.labels.code')}:</b>
            </td>
            <td>{manufacturer.code}</td>
          </tr>
          <tr>
            <td>
              <b>{t('general:manufacturers.labels.short_code')}:</b>
            </td>
            <td>{manufacturer.short_code}</td>
          </tr>
          <tr>
            <td>
              <b>{t('general:manufacturers.labels.alias_plural')}:</b>
            </td>
            <td>{this.renderAliases()}</td>
          </tr>
          <tr>
            <td>
              <b>{t('general:manufacturers.labels.key_oem')}:</b>
            </td>
            <td>{manufacturer.key_oem ? 'Yes' : 'No'}</td>
          </tr>
          <tr>
            <td>
              <b>{t('general:manufacturers.labels.global_list')}:</b>
            </td>
            <td>{manufacturer.global_list ? 'Yes' : 'No'}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  render() {
    const { manufacturersProductsStore, t } = this.props;
    const manufacturer = this.props.manufacturersProductsStore.selectedManufacturer;

    return (
      <React.Fragment>
        {this.renderDetails()}
        <Divider />
        <List className={styles.actions}>
          <ListItem disableGutters={true}>
            <ListItemText
              primary={
                <Typography component="span" variant="title">
                  {manufacturer.product_pending_count}
                </Typography>
              }
              secondary={t('general:products.labels.temporary', {
                count: manufacturer.product_pending_count,
              })}
            />
            <Button
              variant="contained"
              onClick={() => {
                manufacturersProductsStore.setSelectedProductStatus(
                  'Pending Review',
                  'Temporary Products'
                );
              }}
              color="primary"
              disabled={manufacturer.product_pending_count === 0}
            >
              {t('general:actions.view')}
            </Button>
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemText
              primary={
                <Typography component="span" variant="title">
                  {manufacturer.product_approved_count}
                </Typography>
              }
              secondary={t('general:products.labels.approved', {
                count: manufacturer.product_approved_count,
              })}
            />
            <Button
              variant="contained"
              onClick={() => {
                manufacturersProductsStore.setSelectedProductStatus(
                  'Approved',
                  'Approved Products'
                );
              }}
              color="primary"
              disabled={manufacturer.product_approved_count === 0}
            >
              {t('general:actions.view')}
            </Button>
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemText
              primary={
                <Typography component="span" variant="title">
                  {manufacturer.product_not_approved_count}
                </Typography>
              }
              secondary={t('general:products.labels.not_approved', {
                count: manufacturer.product_not_approved_count,
              })}
            />
            <Button
              variant="raised"
              onClick={() => {
                manufacturersProductsStore.setSelectedProductStatus(
                  'Not Approved',
                  'Not Approved Products'
                );
              }}
              color="primary"
              disabled={manufacturer.product_not_approved_count === 0}
            >
              {t('general:actions.view')}
            </Button>
          </ListItem>
          <ListItem disableGutters={true}>
            <ListItemText
              primary={
                <Typography component="span" variant="title">
                  {manufacturer.product_archived_count}
                </Typography>
              }
              secondary={t('general:products.labels.archived', {
                count: manufacturer.product_archived_count,
              })}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                manufacturersProductsStore.setSelectedProductStatus(
                  'Archived',
                  'Archived Products'
                );
              }}
              disabled={manufacturer.product_archived_count === 0}
            >
              {t('general:actions.view')}
            </Button>
          </ListItem>
        </List>
      </React.Fragment>
    );
  }
}
